import { computedFrom, useView, PLATFORM } from 'aurelia-framework';
import { MdcDatepickerDialogConfiguration, IMdcDatepickerDialogOptions } from '@aurelia-toolkit/mdc-datepicker';
import { MdcDialog } from '@aurelia-mdc-web/dialog';
import { getPredefinedIntervals } from './get-predefined-intervals';
import { IPredefinedInterval } from '../mdc-daterange-picker/i-predefined-interval';
import { format, startOfDay, addMonths, isSameMonth, startOfMonth, addDays, isSameDay, isWeekend, lastDayOfMonth } from '../../date-fns-local';

interface IDay {
  date?: Date | null;
  disabled: boolean;
  inRange?: boolean;
  selectedFrom?: boolean;
  selectedTo?: boolean;
  hovered?: boolean;
}

interface IMonth {
  name: string;
  disabled: boolean;
}

export interface IMdcDaterangeDialogData {
  from: Date | null;
  to: Date | null;
  options: Partial<IMdcDatepickerDialogOptions>;
}

@useView(PLATFORM.moduleName('elements/mdc-daterange-dialog/mdc-daterange-dialog.html'))
export class MdcDaterangeDialog {
  constructor(private configuration: MdcDatepickerDialogConfiguration) { }

  dialog: MdcDialog;
  months: IMonth[];

  fromMonth: number;
  fromYear: number;
  toMonth: number;
  toYear: number;
  years: number[];
  weekDays: string[];
  from?: Date;
  to?: Date;
  originalData: Partial<IMdcDaterangeDialogData>;
  options: Partial<IMdcDatepickerDialogOptions>;
  fromDays: IDay[];
  toDays: IDay[];
  predefinedIntervals: IPredefinedInterval[];

  @computedFrom('from', 'to')
  get numberOfDays() {
    if (!this.from || !this.to) return 0;
    return Math.ceil((this.to.getTime() - this.from.getTime()) / (1000 * 3600 * 24));
  }

  activate(data: Partial<IMdcDaterangeDialogData>) {
    this.originalData = data ?? {};
    this.options = { ...this.configuration.defaultOptions, ...data?.options };
    this.options.i18n = { ...this.configuration.defaultOptions.i18n, ...data?.options?.i18n };
    this.weekDays = Array.from({ length: 7 }, (_, i) => format(new Date(2020, 10, 16 + i - 1 + this.options.firstDay), 'EEE', { locale: this.options?.i18n?.dateFnsLocale }));
    this.predefinedIntervals = getPredefinedIntervals((this.options?.firstDay ?? this.configuration.defaultOptions.firstDay) as 0 | 1 | 2 | 3 | 4 | 5 | 6);
    this.from = data.from ? startOfDay(data.from) : undefined;
    this.to = data.to ? startOfDay(data.to) : undefined;
    this.init();
  }

  init() {
    if (this.options.min && this.from < this.options.min) {
      this.from = this.options.min;
    }
    if (this.options.max && this.from > this.options.max) {
      this.from = this.options.max;
    }
    const from = this.from ?? startOfDay(new Date());
    const to = this.to ?? addMonths(from, 1);
    this.fromMonth = from.getMonth();
    this.fromYear = from.getFullYear();
    if (isSameMonth(this.from, this.to)) {
      const startOfNextMonth = addMonths(startOfMonth(this.from), 1);
      this.toMonth = startOfNextMonth.getMonth();
      this.toYear = startOfNextMonth.getFullYear();
    } else {
      this.toMonth = to.getMonth();
      this.toYear = to.getFullYear();
    }
    this.buildMonthsYears();
    this.buildFromDays();
    this.buildToDays();
  }

  buildMonthsYears() {
    const min = Math.max(this.options.min ? this.options.min.getFullYear() : this.options.yearRange.min, this.options.yearRange.min);
    const max = Math.min(this.options.max ? this.options.max.getFullYear() : this.options.yearRange.max, this.options.yearRange.max);
    this.years = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    this.months = Array.from({ length: 12 }, (_, i) => ({
      name: format(new Date(2020, i), 'LLLL', { locale: this.options?.i18n?.dateFnsLocale }),
      disabled: this.isMonthDisabled(this.fromYear, i)
    }));
  }

  buildFromDays() {
    this.fromDays = this.buildDays(this.fromYear, this.fromMonth);
  }

  buildToDays() {
    this.toDays = this.buildDays(this.toYear, this.toMonth);
  }

  buildDays(toYear: number, toMonth: number): IDay[] {
    const first = new Date(toYear, toMonth);
    const offset = this.weekDays.indexOf(format(first, 'EEE', { locale: this.options.i18n.dateFnsLocale }));
    return Array.from({ length: 42 }, (_, i) => {
      const date = addDays(first, i - offset);
      const sameMonth = isSameMonth(first, date);
      const day: IDay = {
        date: this.options.showAll || sameMonth ? date : null,
        disabled: this.isDayDisabled(date, sameMonth),
        selectedFrom: isSameDay(date, this.from),
        selectedTo: isSameDay(date, this.to)
      };
      day.inRange = !day.disabled && day.date && this.from <= date && date <= this.to;
      return day;
    });
  }

  isDayDisabled(date: Date, sameMonth: boolean): boolean {
    return !sameMonth
      || this.options.min && this.options.min > date
      || this.options.max && this.options.max < date
      || this.options.disableFunction?.(date)
      || this.options.disableWeekends && isWeekend(date);
  }

  isMonthDisabled(year: number, month: number) {
    return !!this.options.min && this.options.min > lastDayOfMonth(new Date(year, month))
      || !!this.options.max && this.options.max < new Date(year, month);
  }

  monthSelected(month: number) {
    this.fromMonth = month;
  }

  select(d: IDay) {
    if (d.disabled || !d.date) {
      return;
    }

    // no days selected - set from
    if (!this.from && !this.to) {
      this.from = d.date;
      d.selectedFrom = true;
      return;
    }

    // only from selected
    if (this.from && !this.to) {
      if (d.date >= this.from) {
        this.to = d.date;
        d.selectedTo = true;
      } else {
        this.to = this.from;
        this.from = d.date;
        d.selectedFrom = true;
      }
      this.refreshDaysFlags();
      return;
    }

    // both dates selected
    if (this.from && this.to) {
      if (isSameMonth(this.from, this.to) && isSameMonth(this.from, d.date)) {
        this.from = d.date;
        this.to = undefined;
        this.refreshDaysFlags();
        return;
      } else {
        // depending on which calendar was selected set either from or to date
        if (this.fromDays.includes(d)) {
          this.from = d.date;
        } else {
          this.to = d.date;
        }
        this.refreshDaysFlags();
      }
      return;
    }
  }

  ok() {
    this.originalData.from = this.from;
    this.originalData.to = this.to;
    this.dialog.close('ok');
  }

  @computedFrom('fromYear', 'fromMonth')
  get canGoPrevFrom() {
    const prevMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), -1);
    return this.isMonthDisabled(prevMonthStart.getFullYear(), prevMonthStart.getMonth());
  }

  prevFrom() {
    const prevMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), -1);
    [this.fromYear, this.fromMonth] = [prevMonthStart.getFullYear(), prevMonthStart.getMonth()];
  }

  @computedFrom('fromYear', 'fromMonth')
  get canGoNextFrom() {
    const nextMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), 1);
    return this.isMonthDisabled(nextMonthStart.getFullYear(), nextMonthStart.getMonth());
  }

  nextFrom() {
    const nextMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), 1);
    [this.fromYear, this.fromMonth] = [nextMonthStart.getFullYear(), nextMonthStart.getMonth()];
    if (this.fromYear === this.toYear && this.fromMonth === this.toMonth) {
      this.nextTo();
    }
  }

  @computedFrom('toYear', 'toMonth')
  get canGoPrevTo() {
    const prevMonth = addMonths(new Date(this.toYear, this.toMonth), -1);
    return this.isMonthDisabled(prevMonth.getFullYear(), prevMonth.getMonth());
  }

  prevTo() {
    const prevMonth = addMonths(new Date(this.toYear, this.toMonth), -1);
    [this.toYear, this.toMonth] = [prevMonth.getFullYear(), prevMonth.getMonth()];
    if (this.fromYear === this.toYear && this.fromMonth === this.toMonth) {
      this.prevFrom();
    }
  }

  @computedFrom('toYear', 'toMonth')
  get canGoNextTo() {
    const nextMonthStart = addMonths(new Date(this.toYear, this.toMonth), 1);
    return this.isMonthDisabled(nextMonthStart.getFullYear(), nextMonthStart.getMonth());
  }

  nextTo() {
    const nextMonthStart = addMonths(new Date(this.toYear, this.toMonth), 1);
    [this.toYear, this.toMonth] = [nextMonthStart.getFullYear(), nextMonthStart.getMonth()];
  }

  handleMouseover(d: IDay) {
    if (!this.from && !this.to || !d.date) {
      return;
    }
    this.refreshDaysFlags(d);
  }

  handleMouseLeave() {
    this.allDays().forEach(x => x.hovered = false);
  }

  private allDays() {
    return [...this.fromDays, ...this.toDays];
  }

  handleMenuSelected(pi: IPredefinedInterval) {
    this.from = pi.from;
    this.to = pi.to;
    this.init();
    this.ok();
  }

  refreshDaysFlags(hoveredDay?: IDay) {
    this.allDays().forEach(x => {
      x.selectedFrom = isSameDay(x.date, this.from);
      x.selectedTo = isSameDay(x.date, this.to);
      x.inRange = this.from <= x.date && x.date <= this.to;
      x.hovered = hoveredDay && (!this.from || !this.to)
        && !x.inRange
        && ((hoveredDay.date <= x.date && x.date <= (this.from ?? this.to))
          || ((this.from ?? this.to) <= x.date && x.date <= hoveredDay.date));
    });
  }

  clear() {
    this.from = undefined;
    this.to = undefined;
    this.refreshDaysFlags(undefined);
  }
}
